import { clean, num } from "./format";
import type { Answer, LocalizedText, Rng } from "./types";

export type Choice4 = Extract<Answer, { mode: "choice4" }>;

/**
 * Build a 4-option multiple-choice answer from a correct number and a list of
 * "plausible mistake" candidates. Candidates equal to the correct value, duplicates
 * and non-finite values are dropped; the fallback fills the remaining slots.
 */
export function makeChoices(
  rng: Rng,
  correct: number,
  candidates: number[],
  fallback: (rng: Rng) => number,
  format: (n: number) => LocalizedText = num,
): Choice4 {
  const correctClean = clean(correct);
  const seen = new Set<number>([correctClean]);
  const distractors: number[] = [];

  for (const c of candidates) {
    const v = clean(c);
    if (!Number.isFinite(v) || seen.has(v)) continue;
    seen.add(v);
    distractors.push(v);
    if (distractors.length === 3) break;
  }

  let guard = 0;
  while (distractors.length < 3 && guard++ < 200) {
    const v = clean(fallback(rng));
    if (!Number.isFinite(v) || seen.has(v)) continue;
    seen.add(v);
    distractors.push(v);
  }
  if (distractors.length < 3) {
    throw new Error("makeChoices: could not build 3 unique distractors");
  }

  const options = rng.shuffle([correctClean, ...distractors]);
  return {
    mode: "choice4",
    choices: options.map(format),
    correctIndex: options.indexOf(correctClean),
  };
}

/**
 * Multiple choice for non-numeric options (e.g. shape names). `others` should have ≥ 3 unique
 * entries; when it does not, `fallback(i)` is asked for more (it must eventually produce new text).
 */
export function makeTextChoices(
  rng: Rng,
  correct: LocalizedText,
  others: LocalizedText[],
  fallback?: (i: number) => LocalizedText,
): Choice4 {
  const unique = others.filter(
    (o, i, arr) => o.en !== correct.en && arr.findIndex((x) => x.en === o.en) === i,
  );
  let guard = 0;
  while (unique.length < 3 && fallback && guard < 60) {
    const extra = fallback(guard++);
    if (extra.en !== correct.en && !unique.some((x) => x.en === extra.en)) unique.push(extra);
  }
  if (unique.length < 3) throw new Error("makeTextChoices: need at least 3 unique distractors");
  const picked = rng.shuffle(unique).slice(0, 3);
  const options = rng.shuffle([correct, ...picked]);
  return { mode: "choice4", choices: options, correctIndex: options.indexOf(correct) };
}

/** Common integer-mistake candidates around a correct value. */
export function nearMisses(correct: number, spread = 1): number[] {
  return [correct + spread, correct - spread, correct + 10, correct - 10, -correct, correct * 2];
}
